<template>
  <div id="AdminAccounts" class="container">
    <div class="row mb-3 mt-4">
      <div class="col">
        <h1>Accounts</h1>
      </div>
      <div class="col-auto d-flex gap-3 align-items-center">
        <select
          class="form-select"
          v-model="selectedCountry"
          @change="filterAccounts"
        >
          <option value="">All Countries</option>
          <option
            v-for="country in availableCountries"
            :key="country"
            :value="country"
          >
            {{ country }}
          </option>
        </select>
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="showOnlyRegistered"
            @change="filterAccounts"
            id="registeredFilter"
          />
          <label class="form-check-label text-nowrap" for="registeredFilter">
            Show only registered
          </label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="showDeleted"
            @change="filterAccounts"
            id="deletedFilter"
          />
          <label class="form-check-label text-nowrap" for="deletedFilter">
            Show deleted
          </label>
        </div>
        <button
          v-if="isManagingAccount"
          class="btn btn-secondary text-nowrap"
          @click="stopAccountManaging()"
        >
          Reset Managing
        </button>
        <button
          class="btn btn-primary text-nowrap"
          @click="sendRetentionNotification"
          :disabled="isSendingNotification"
        >
          {{
            isSendingNotification ? "Sending..." : "Send Retention Notification"
          }}
        </button>
      </div>
    </div>
    <div class="table-container table-responsive scrollbar">
      <table id="account-table" class="table table-hover align-middle p-2">
        <thead>
          <tr>
            <th>ID</th>
            <th>Actions</th>
            <th>Account</th>
            <th>
              Company - ID - Registry code - Has registered - Registry devices
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="account in filteredAccounts"
            :key="account.id"
            :class="{
              'table-active':
                account.id === accountStore.account.managing_account_id,
            }"
            :id="`account-row-${account.id}`"
          >
            <td>{{ account.id }}</td>
            <td>
              <button
                class="btn btn-primary btn-sm"
                v-if="account.id"
                @click="onclick(account.id)"
              >
                Manage
              </button>
            </td>
            <td class="col-2">
              {{ account.email }}
              <span
                v-if="account.internal_role === 'super_admin'"
                class="badge rounded-pill bg-primary text-white"
                >Admin
              </span>
              <span
                v-else-if="account.internal_role === 'sales_admin'"
                class="badge rounded-pill text-bg-info"
                >Sales
              </span>
              <br />
              {{ account.name }} <br />
              {{ account.phone_number }}
            </td>
            <td class="table-company-list">
              <div
                v-for="company in account.companies.filter(isCompanyVisible)"
                :key="`${account.id}-company-${company.id}`"
                class="company-item mb-2"
                :class="[
                  getRegistryConnectionStatus(company),
                  { 'company-deleted': company.deleted_at },
                ]"
              >
                <div class="company-header">
                  <button
                    v-if="account.id"
                    class="btn btn-sm p-0 me-2 toggle-btn"
                    @click="toggleCompanyDetails(account.id, company.id!)"
                    type="button"
                  >
                    <font-awesome-icon
                      :icon="
                        isCompanyExpanded(account.id, company.id!)
                          ? 'fa-solid fa-chevron-down'
                          : 'fa-solid fa-chevron-right'
                      "
                    />
                  </button>
                  <span>
                    {{ company.name }} - {{ company.id }} -
                    {{ company.registry_code }} - has_registered:
                    {{ company.has_registered }} - registry_devices:
                    {{ company.registry_devices.length }}
                  </span>
                </div>
                <div
                  v-if="
                    account.id && isCompanyExpanded(account.id, company.id!)
                  "
                  class="company-details mt-2 ms-4"
                >
                  <RegistryConnectionsDisplayTable
                    :registryConnections="company.registry_connections"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import RegistryConnectionsDisplayTable from "@/admin/components/RegistryConnectionsDisplayTable.vue";
import { manageAccount, stopManaging } from "@/admin/manageAccount";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  Account,
  Company,
  RegistryConnection,
  RegistryDevice,
} from "@/types";
import { storeToRefs } from "pinia";
import { nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface AdminAccountProjection {
  companies: (Company & AdminCompanyProjection)[];
}

interface AdminCompanyProjection {
  registry_devices: (RegistryDevice & AdminRegistryDeviceProjection)[];
  registry_connections: RegistryConnection[];
}

interface AdminRegistryDeviceProjection {
  total_cert_count: number;
}

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const accounts = ref<(Account & AdminAccountProjection)[]>([]);
const filteredAccounts = ref<(Account & AdminAccountProjection)[]>([]);
const selectedCountry = ref(
  localStorage.getItem("admin_accounts_country") || ""
);
const showOnlyRegistered = ref(
  localStorage.getItem("admin_accounts_show_registered") === "true"
);
const showDeleted = ref(
  localStorage.getItem("admin_accounts_show_deleted") === "true"
);
const availableCountries = ref<string[]>([]);
const router = useRouter();
const route = useRoute();
const accountStore = useAccountStore();
const { isManagingAccount } = storeToRefs(accountStore);
const isSendingNotification = ref(false);

// Track expanded companies using Set for efficient lookup
const expandedCompanies = ref<Set<string>>(new Set());

const getCompanyKey = (accountId: number, companyId: number): string => {
  return `${accountId}-${companyId}`;
};

const isCompanyExpanded = (accountId: number, companyId: number): boolean => {
  return expandedCompanies.value.has(getCompanyKey(accountId, companyId));
};

const toggleCompanyDetails = (accountId: number, companyId: number) => {
  const key = getCompanyKey(accountId, companyId);
  if (expandedCompanies.value.has(key)) {
    expandedCompanies.value.delete(key);
  } else {
    expandedCompanies.value.add(key);
  }
};

const getRegistryConnectionStatus = (
  company: Company & AdminCompanyProjection
): string => {
  const connections = company.registry_connections || [];

  // Red: No registry connections
  if (connections.length === 0) {
    return "status-red";
  }

  // Check if all connections are active
  const allActive = connections.every((conn) => conn.is_active);
  const hasInactive = connections.some((conn) => !conn.is_active);

  // Yellow: At least one inactive connection
  if (hasInactive) {
    return "status-yellow";
  }

  // Green: All connections are active
  if (allActive) {
    return "status-green";
  }

  return "";
};

const updateAvailableCountries = (
  accounts: (Account & AdminAccountProjection)[]
) => {
  const countries = new Set<string>();
  accounts.forEach((account) => {
    account.companies.forEach((company) => {
      if (company.country) {
        countries.add(company.country);
      }
    });
  });
  availableCountries.value = Array.from(countries).sort();
};

const isCompanyVisible = (
  company: Company & AdminCompanyProjection
): boolean => {
  if (!showDeleted.value && company.deleted_at) return false;
  if (selectedCountry.value && company.country !== selectedCountry.value)
    return false;
  if (showOnlyRegistered.value && !company.has_registered) return false;
  return true;
};

const filterAccounts = () => {
  localStorage.setItem("admin_accounts_country", selectedCountry.value);
  localStorage.setItem(
    "admin_accounts_show_registered",
    showOnlyRegistered.value.toString()
  );
  localStorage.setItem(
    "admin_accounts_show_deleted",
    showDeleted.value.toString()
  );

  filteredAccounts.value = accounts.value.filter((account) =>
    account.companies.some(isCompanyVisible)
  );
};

const fetchAccounts = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/accounts");
    if (response.status === 200 && response.data) {
      accounts.value = response.data;
      updateAvailableCountries(accounts.value);
      filterAccounts();
    } else {
      throw new Error("Error fetching accounts");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const onclick = async (id: number) => {
  try {
    await manageAccount(id);
    await router.push({ name: "Root" });
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const stopAccountManaging = async () => {
  try {
    await stopManaging();
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const scrollToAccount = (accountId: number) => {
  nextTick(() => {
    const element = document.getElementById(`account-row-${accountId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight the row temporarily
      element.classList.add("highlight-row");
      setTimeout(() => {
        element.classList.remove("highlight-row");
      }, 3000);
    }
  });
};

const checkForAccountIdParam = () => {
  const accountIdParam = route.query.account_id;
  if (accountIdParam) {
    const accountId = Number(accountIdParam);
    if (!isNaN(accountId)) {
      // Clear country filter to ensure the account is visible
      selectedCountry.value = "";
      // Re-filter accounts with cleared filter
      filterAccounts();

      // If accounts are already loaded, scroll to the account
      if (filteredAccounts.value.length > 0) {
        scrollToAccount(accountId);
      }
    }
  }
};

const sendRetentionNotification = async () => {
  try {
    isSendingNotification.value = true;
    const response = await goSolidApi.post(
      "/api/admin/retention-notification",
      {
        country: selectedCountry.value || null,
      }
    );
    if (response.status === 200) {
      globalSuccessHandler(
        "Success",
        "Retention notifications sent successfully!"
      );
    } else {
      throw new Error("Failed to send retention notifications");
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSendingNotification.value = false;
  }
};

onMounted(() => {
  fetchAccounts().then(() => {
    checkForAccountIdParam();
  });
});

// Watch for changes in the route query parameters
watch(
  () => route.query.account_id,
  (newAccountId) => {
    if (newAccountId) {
      const accountId = Number(newAccountId);
      if (!isNaN(accountId)) {
        // Clear country filter to ensure the account is visible
        selectedCountry.value = "";
        // Re-filter accounts with cleared filter
        filterAccounts();

        scrollToAccount(accountId);
      }
    }
  }
);
</script>

<style scoped lang="scss">
#AdminAccounts {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

.table-container {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
  .table > tbody > tr > td:first-child {
    padding-left: 1rem;
  }
}

#account-table {
  margin-bottom: 0;
  table-layout: fixed;
  width: 100%;

  .table-active {
    background-color: var(--phoenix-warning-bg-subtle);
    td {
      background-color: #fff3cd;
      box-shadow: none;
    }
  }

  th,
  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th:nth-child(1) {
    width: 5%;
  } /* ID */
  th:nth-child(2) {
    width: 10%;
  } /* Actions */
  th:nth-child(3) {
    width: 20%;
  } /* Account */
  th:nth-child(4) {
    width: 45%;
  } /* Company list */
  th:nth-child(5) {
    width: 20%;
  } /* Meta */

  .table-company-list {
    word-wrap: break-word;
    white-space: normal;
    padding: 0.5rem;

    .company-item {
      padding: 0.5rem;
      border-radius: 0.25rem;
      transition: background-color 0.2s ease;

      &.status-red {
        background-color: rgba(220, 53, 69, 0.1);
        border-left: 3px solid var(--bs-danger);
      }

      &.status-yellow {
        background-color: rgba(255, 193, 7, 0.1);
        border-left: 3px solid var(--bs-warning);
      }

      &.status-green {
        background-color: rgba(25, 135, 84, 0.1);
        border-left: 3px solid var(--bs-success);
      }

      &.company-deleted {
        opacity: 0.5;
        text-decoration: line-through;
      }

      .company-header {
        display: flex;
        align-items: center;
        font-size: 0.9rem;

        .toggle-btn {
          background: none;
          border: none;
          color: var(--bs-body-color);
          font-size: 0.8rem;
          cursor: pointer;
          padding: 0.2rem 0.4rem;
          line-height: 1;

          &:hover {
            color: var(--phoenix-primary);
            background-color: var(--phoenix-body-highlight-bg);
            border-radius: 0.25rem;
          }

          &:focus {
            outline: none;
            box-shadow: none;
          }
        }
      }

      .company-details {
        padding: 0.5rem;
        background-color: var(--bs-light);
        border-radius: 0.25rem;
      }
    }
  }
}

@media (max-width: 1200px) {
  #account-table {
    th:nth-child(1) {
      width: 8%;
    }
    th:nth-child(2) {
      width: 12%;
    }
    th:nth-child(3) {
      width: 25%;
    }
    th:nth-child(4) {
      width: 35%;
    }
    th:nth-child(5) {
      width: 20%;
    }
  }
}

@media (max-width: 768px) {
  #account-table {
    th:nth-child(1) {
      width: 10%;
    }
    th:nth-child(2) {
      width: 15%;
    }
    th:nth-child(3) {
      width: 30%;
    }
    th:nth-child(4) {
      width: 30%;
    }
    th:nth-child(5) {
      width: 15%;
    }
  }
}

.form-select {
  min-width: 150px;
}

.highlight-row {
  animation: highlight-animation 3s ease-in-out;
}

@keyframes highlight-animation {
  0% {
    background-color: rgba(255, 193, 7, 0.5);
  }
  100% {
    background-color: transparent;
  }
}

.btn {
  white-space: nowrap;
  min-width: fit-content;
}
</style>
